// Client for the lead-enrichment API (/api/enrich/*). Like gmail.js there is no
// SAMPLE-DATA fallback — real OpenAI + Apollo enrichment can't be simulated, so
// when the API isn't reachable the UI shows an honest "not available" state
// instead of fake results. There IS a deployed-API fallback (apiFetch below,
// same pattern as hubspot.js): plain local dev has no /api proxy, so when the
// same-origin path answers with index.html the request retries against the
// deployed stack with the same auth — real research from a laptop, never fake
// research from anywhere.
//
// The pipeline is async: `startEnrich` enqueues up to 10 rows and returns a
// batchId; the caller polls `enrichStatus(batchId)` until every job is done.
// Identity is the Cognito ID token (Bearer) — the server never trusts a field.
import { requireIdToken } from './cognito.js'
import { keyOf } from './companyMeta.js'
import { scopedKey } from './userScope.js'

// Hard client-side cap (the server enforces the same). Keeps OpenAI/Apollo
// spend bounded per run.
export const MAX_ENRICH_ROWS = 10

async function authHeaders(extra = {}) {
  const token = await requireIdToken()
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function apiJson(res) {
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    const err = new Error(`Enrichment API returned ${res.status} (not JSON)`)
    err.status = res.status
    throw err
  }
  const data = await res.json()
  if (!res.ok) {
    // Keep the server's own words AND the status: the caller needs to tell an
    // expired session (401) from a missing key (409) from a bad payload (400).
    const err = new Error(data.error || `Enrichment API error (${res.status})`)
    err.status = res.status
    throw err
  }
  return data
}

// Local dev has no /api proxy — the dev server's history fallback answers
// /api/enrich/* with index.html and a 200 — so, exactly like hubspot.js get()
// and data.jsx, the client falls through to the deployed API when the
// same-origin response is not really the API (not JSON). Auth still applies
// (the same Cognito bearer token rides along on the retry), so this changes
// WHERE the request goes, never WHO may ask — the server verifies the token on
// every route either way.
const ENRICH_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

// Every request in this module goes through here: same-origin first, then the
// deployed API when the same-origin answer is not JSON. `pathAndQuery` is the
// route after /enrich/ ("start", "lead?…"); `init` carries method/headers/body
// — and, when the caller passed one, an AbortSignal — unchanged onto the
// retry. The signal matters to the agent engine: a cancelled run must be able
// to interrupt an in-flight enrich request instead of waiting out the network
// stack's own timeout.
async function apiFetch(pathAndQuery, init) {
  let res = await fetch(`/api/enrich/${pathAndQuery}`, init)
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json') && ENRICH_FALLBACK_BASE) {
    res = await fetch(`${ENRICH_FALLBACK_BASE}/enrich/${pathAndQuery}`, init)
  }
  return apiJson(res)
}

// { available, configured, apollo } — whether enrichment can run at all.
export async function enrichConfig() {
  try {
    const data = await apiFetch('config', { headers: await authHeaders() })
    return { available: true, ...data }
  } catch {
    return { available: false, configured: false, apollo: false }
  }
}

// Enqueue up to MAX_ENRICH_ROWS rows. Returns { configured, batchId, jobs }.
// Throws when the API is unreachable so the caller can surface it. `signal`
// (optional) aborts the request itself — the agent engine passes its run
// signal so Cancel interrupts a request already on the wire.
// ── the research ledger: a lead is researched ONCE, ever ────────────────────
//
// Research costs OpenAI + Apollo credits, so the expensive call must be
// idempotent per lead — across re-runs, across chats, across reloads. Two
// layers, because they fail differently:
//
//   1. THE LEDGER (persistent, per account). Every lead we have enqueued or
//      resolved is written here. startEnrich REFUSES a lead already in it, so
//      a second run of the same operation — or the same lead appearing in two
//      conversations — cannot spend twice. It survives reloads because
//      localStorage does, which is the whole point: a refresh is the cheapest
//      way to accidentally re-buy 50 dossiers.
//   2. THE IN-FLIGHT MAP (module scope, per session). Fifty cards asking for
//      the same lead in the same tick share ONE request instead of racing
//      fifty. Cleared when the promise settles, so a failure can be retried.
//
// The ledger deliberately records an ATTEMPT, not just a success: a lead whose
// research came back empty was still paid for, and re-enqueueing it would pay
// again for the same empty answer. A caller that genuinely wants to re-buy a
// dossier calls forgetResearched(key) first — an explicit act, never implicit.
const LEDGER_BASE = 'revailosales.researchLedger'
const ledgerKey = () => scopedKey(LEDGER_BASE)

// Bounded: the ledger is a spend guard, not an archive. Oldest entries fall off
// first — a lead researched a very long time ago is the one most worth
// re-researching anyway.
const LEDGER_MAX = 5000

function readLedger() {
  try {
    const raw = JSON.parse(localStorage.getItem(ledgerKey()) || '{}')
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  } catch {
    return {}
  }
}

function writeLedger(entries) {
  try {
    const keys = Object.keys(entries)
    if (keys.length > LEDGER_MAX) {
      // Drop the oldest by recorded time; ties keep insertion order.
      const sorted = keys.sort((a, b) => (entries[a]?.at || 0) - (entries[b]?.at || 0))
      for (const k of sorted.slice(0, keys.length - LEDGER_MAX)) delete entries[k]
    }
    localStorage.setItem(ledgerKey(), JSON.stringify(entries))
  } catch {
    /* a full store must never break a run — the worst case is paying twice */
  }
}

/** Has this lead already been researched (or paid for) by this account? */
export function isResearched(leadKey) {
  const k = String(leadKey || '')
  return Boolean(k && readLedger()[k])
}

/** Record a lead as researched/attempted. `mode` is informational only. */
export function markResearched(leadKey, mode = '') {
  const k = String(leadKey || '')
  if (!k) return
  const entries = readLedger()
  // First write wins on `at`: the ledger answers "have we paid for this", and
  // the original purchase is the honest timestamp for that.
  if (!entries[k]) entries[k] = { at: Date.now(), mode: String(mode || '') }
  writeLedger(entries)
}

/** Explicitly allow re-buying one lead's dossier. */
export function forgetResearched(leadKey) {
  const k = String(leadKey || '')
  if (!k) return
  const entries = readLedger()
  if (entries[k]) {
    delete entries[k]
    writeLedger(entries)
  }
}

/** How many leads this account has already paid to research. */
export function researchedCount() {
  return Object.keys(readLedger()).length
}

// ── ledger self-heal: an entry that guards nothing must not block research ──
//
// The attempt-not-success policy above STILL HOLDS, and this does not weaken
// it: a lead that really was researched and came back thin — or came back with
// nothing interesting to say — was paid for, and re-enqueueing it buys the same
// empty answer a second time. A dossier that EXISTS keeps its ledger entry
// forever, however thin that dossier is.
//
// What the policy never accounted for is an attempt that bought nothing at all.
// When the workspace's OpenAI key ran out of credit, ~130 leads were enqueued,
// ledgered at enqueue time (correctly — we cannot know from here whether the
// vendor billed us), and then every one of their jobs died on a 429 before a
// single token was charged. Those leads are now marked researched-forever with
// no dossier to show for it: the cards stay blank, and nothing the user can do
// in the UI will ever ask for them again.
//
// The client cannot observe "did the vendor charge us". It CAN observe, for
// free, the question that actually matters: does the workspace hold a dossier
// for this lead? GET /enrich/lead answers that authoritatively and without
// spending. So the rule is:
//
//   ledgered + API says found:true   → KEEP. Research exists. Never re-buy.
//   ledgered + found:false + pending → KEEP. There is no dossier YET because
//                                      the research is running right now — the
//                                      scheduled backfill keeps 50-200 leads in
//                                      flight at a time, and every one of them
//                                      is a cache miss until its worker
//                                      finishes. Forgetting here would drop the
//                                      entry, let the very next agent pass
//                                      re-enqueue the lead, and buy the SAME
//                                      dossier a second time — the one failure
//                                      mode this whole self-heal could create
//                                      that costs more than it repairs. The
//                                      client cannot see job rows, so the
//                                      server states it: GET /enrich/lead
//                                      answers { found:false, pending:true }
//                                      when a live in-flight marker exists for
//                                      the lead.
//   ledgered + API says found:false  → FORGET. The entry is guarding a dossier
//     (and NOT pending)                that does not exist and that nobody is
//                                      currently buying; all it can do is block
//                                      the repair.
//   ledgered + NO answer at all      → KEEP. A lookup that failed (offline,
//     (network, 401, 5xx, HTML)        expired session, gateway HTML) is an
//                                      UNKNOWN, not a "no". Reading it as a no
//                                      would empty the ledger the first time
//                                      the API blips and re-buy every dossier
//                                      we own — the single most expensive
//                                      mistake available here. That is why the
//                                      verdict takes an explicit `answered`
//                                      flag and never infers one from a falsy
//                                      `found`.
//   not ledgered                     → nothing to do; untouched.
//
// And one race guard: a lead enqueued MOMENTS ago is legitimately ledgered with
// no dossier yet, because the worker is still running. Entries younger than
// SELF_HEAL_GRACE_MS are left alone — the same 30-minute window loadActiveBatch
// below uses to decide a batch is over. The 130 poisoned entries are days old.
//
// This is per lead, at the moment we look that lead up. The ledger is never
// cleared wholesale, here or anywhere.
export const SELF_HEAL_GRACE_MS = 30 * 60 * 1000

/**
 * The whole rule, as one pure function so it can be tested without a network.
 *   `entry`  — the ledger record for this lead, or null/undefined when unledgered.
 *   `lookup` — { answered, found, pending }. `answered` MUST be true only when
 *              the API gave a real, parsed verdict about this lead; `pending`
 *              is the server saying research for this lead is running NOW.
 * Returns 'keep' or 'forget'.
 */
export function ledgerSelfHealVerdict(entry, lookup, now = Date.now()) {
  if (!entry) return 'keep' // not ledgered — nothing to heal
  if (!lookup || lookup.answered !== true) return 'keep' // unknown ≠ "no dossier"
  if (lookup.found) return 'keep' // researched, however thin: stays paid-for
  // Somebody is buying this dossier right now (the backfill, another tab). The
  // ledger entry is guarding a purchase in progress, which is exactly what it
  // is for — dropping it here is how the same lead gets researched twice. This
  // check sits ABOVE the grace window on purpose: the window only knows how old
  // OUR entry is, and the run that is paying may have started long after it.
  if (lookup.pending) return 'keep'
  if (now - Number(entry.at || 0) < SELF_HEAL_GRACE_MS) return 'keep' // still in flight
  return 'forget'
}

/**
 * Apply the verdict to one lead. Returns true when the entry was dropped (i.e.
 * this lead may be researched again). Never touches any other entry.
 */
export function healResearchLedger(leadKey, lookup) {
  const k = String(leadKey || '')
  if (!k) return false
  if (ledgerSelfHealVerdict(readLedger()[k], lookup) !== 'forget') return false
  forgetResearched(k)
  return true
}

// One in-flight request per lead key, shared by every concurrent caller.
const _leadInFlight = new Map()

export async function startEnrich(rows, category, { signal } = {}) {
  // `rowKey` rides along so status responses can be matched back to the exact
  // table rows — company names are not unique and get normalized server-side.
  // THE SPEND GATE. A lead already in the ledger has been paid for once and is
  // never enqueued again — this is what makes a re-run, a second chat, or a
  // refresh free. Skipped leads are REPORTED (not silently dropped) so a caller
  // can say "12 already researched" instead of pretending it asked for them.
  const all = (rows || []).map((r) => ({ ...r, rowKey: keyOf(r) }))
  const alreadyResearched = all.filter((r) => isResearched(r.rowKey)).map((r) => r.rowKey)
  const fresh = all.filter((r) => !isResearched(r.rowKey))
  const capped = fresh.slice(0, MAX_ENRICH_ROWS)
  if (!capped.length) {
    // Nothing LEFT to enqueue is not the same as nothing asked for: when every
    // row was already researched this is a success with zero spend, not an
    // error a caller should surface as a failure.
    if (alreadyResearched.length) {
      return { configured: true, batchId: '', jobs: [], skipped: alreadyResearched, spent: false }
    }
    const err = new Error('Nothing selected to enrich')
    err.status = 400
    throw err
  }
  const headers = await authHeaders({ 'Content-Type': 'application/json' })
  if (!headers.Authorization) {
    // The API identifies the caller from the Cognito ID token; without one it
    // answers 401 in a couple of milliseconds and the old UI blamed "the
    // deployed API". Say what is actually wrong.
    const err = new Error('You are signed out — sign in again to enrich')
    err.status = 401
    throw err
  }
  const started = await apiFetch('start', {
    method: 'POST',
    headers,
    body: JSON.stringify({ category, rows: capped }),
    signal,
  })
  // Written the moment the server accepts the batch, NOT when the jobs finish:
  // a run that is cancelled or reloaded mid-batch has still spent the credits,
  // and the ledger exists to stop the second purchase.
  for (const row of capped) markResearched(row.rowKey, 'fresh')
  return { skipped: alreadyResearched, spent: true, ...started }
}

// Which leads in a category already hold a fresh dossier — KEYS ONLY, one
// call. This is the hint that lets an operation put researched leads in the
// batch it is about to draft, instead of cutting the batch first and finding
// out afterwards that none of the fifty had been researched. Fails soft to an
// empty set: no hint simply means "rank the way you always did".
export async function researchedKeys(category) {
  try {
    const qs = new URLSearchParams({ category: category || '' })
    // apiFetch ALREADY returns parsed JSON (it ends in `return apiJson(res)`).
    // This used to wrap it in a second apiJson(), which called `.headers.get`
    // on a plain object, threw TypeError on every single call, and was
    // swallowed by the catch below — so this function returned an EMPTY SET
    // every time it was ever called. Its one caller is the "researched leads
    // go first" reorder in agentRun.js, which therefore never fired: a run
    // could cut its fifty on score alone while paid-for dossiers sat unused in
    // the workspace cache, which is the exact failure that reorder exists to
    // prevent. Never re-parse an apiFetch result.
    const data = await apiFetch(`researched?${qs}`, { headers: await authHeaders() })
    return new Set((data.keys || []).map((k) => String(k)))
  } catch {
    return new Set()
  }
}

// The stored dossier for one lead, from whoever enriched it — so the lead page
// can show research this browser never ran. { found, result, rankedScore, ... }
export async function enrichLead(category, key, { signal } = {}) {
  const id = `${category || ''}::${key || ''}`
  // Fifty cards, one lead, one request. The map holds the PROMISE, so callers
  // that arrive mid-flight await the same answer instead of opening their own
  // connection.
  const shared = _leadInFlight.get(id)
  if (shared) return shared
  const request = (async () => {
    const qs = new URLSearchParams({ category: category || '', key: key || '' })
    const data = await apiFetch(`lead?${qs}`, { headers: await authHeaders(), signal })
    // THE ANSWER POINT — the one place the client learns, authoritatively and
    // for free, whether a dossier exists for this lead. Both ledger directions
    // hang off it, and only off a genuinely parsed verdict:
    //
    //   found:true  → a dossier EXISTS and somebody paid for it. Record it so
    //                 no later run enqueues this lead again.
    //   found:false → the workspace holds NO dossier. If the ledger says we
    //                 already paid for this lead, that entry is guarding
    //                 nothing (the credit-outage case) and must drop out so the
    //                 lead can be researched again. See healResearchLedger.
    //   pending     → …UNLESS the server says research for this lead is in
    //                 flight right now. Then there is no dossier YET, the entry
    //                 is guarding a purchase already being made, and dropping
    //                 it would buy the same dossier twice.
    //
    // A lookup that FAILED never reaches this line at all — apiFetch throws on
    // a transport error, a non-JSON answer, or any non-2xx — so a failure is
    // structurally incapable of forgetting anything. That is deliberate: an
    // unanswered lookup is an unknown, and re-buying research on an unknown is
    // the expensive mistake. `answered` is likewise proven, not assumed: a
    // body without a boolean `found` is treated as no answer.
    const answered = Boolean(data) && typeof data === 'object' && typeof data.found === 'boolean'
    if (answered && data.found) markResearched(key, 'cached')
    else if (answered) {
      // `pending` is only ever honoured when it is literally true — an older
      // API build omits the field entirely, and a missing field must read as
      // "not pending", never as an excuse to skip the heal.
      healResearchLedger(key, { answered: true, found: false, pending: data.pending === true })
    }
    return data
  })()
  _leadInFlight.set(id, request)
  try {
    return await request
  } finally {
    _leadInFlight.delete(id)
  }
}

// ── the draft: one outreach email, WRITTEN BY THE MODEL on the server ───────
//
// POST /enrich/draft (enrich/handler.py#_draft). The lambda reads the FULL
// cached dossier for the lead itself — the receipt only ever holds capped
// copies — adds the row facts sent here, and has the model write the email
// under the same rules the browser template enforced (nothing invented, one
// attributed number, no durations, no AI tells). It answers with the text, the
// facts it used (`sources`, in the shape the provenance already reads) and an
// `exec` block naming exactly what code ran: file, functions, Lambda, model,
// prompt id + sha, source sha. That block is stored on the receipt next to the
// draft and is what the Developer view shows as "the code that wrote this".
//
// SPEND: one cheap chat completion per lead (no web tool — the model may only
// use what it is given). Callers bound concurrency; the account's Lambda
// concurrency is small.
//
// A failure THROWS — the caller decides what to do (LeadCards' resolver falls
// back to the browser template and labels the fallback as such); nothing here
// ever pretends the model wrote something it did not.
//
// ── TWO SUCCESSFUL ANSWERS, NOT ONE: the email, or the REFUSAL ──────────────
//
// The lambda is now shown the real thread behind the touch (`thread`, from
// mailHistory.js#threadMessages by way of the lead stub), and a writer that can
// READ the conversation can also see that there is no sale in it. So it is
// allowed to decline, and says so in the body:
//
//   { ok, subject, bodyText, sources, exec }              an email
//   { ok, skip:true, reason, category, exec }             no email, and why not
//
// BOTH ARE 200s AND NEITHER IS A FAILURE. The refusal is a decision the system
// made about a real person — "this thread is IT and payroll admin with a
// colleague, not a sales conversation" — and it is the whole point of showing
// the model the thread: a follow-up written from the dossier alone will always
// find something to pitch, including to our own co-founder on a thread about a
// docking station. That is the bug this route exists to stop.
//
// This function used to turn EVERY emailless 200 into `new Error('The draft API
// returned no email')`, which threw the reason away with the body: the caller
// got an Error with no status and no fields, could not tell "the server read the
// thread and declined" from "the model came back empty", and its only safe move
// was the browser template — the pitch the refusal exists to prevent. So a
// refusal is now RETURNED WHOLE, `reason` and `category` and `exec` intact, and
// the caller branches on `skip`. Nothing else about the shape changed, so a
// caller that only ever looks at `subject`/`bodyText` still sees an answer with
// neither and must still decide what to do about it.
//
// NOTE ON `category`: on a refusal the body's `category` is the SKIP category
// (internal | vendor | support | personal | recruiting | not-a-prospect), NOT
// the lead category that was sent in the request. Two different things under
// one name, which is the lambda's contract and not ours to rename here — it is
// written down so nobody reads a refused draft's `category` as the campaign's.
//
// ONE REQUEST PER LEAD AT A TIME. Two callers asking for the same lead's draft
// while one is on the wire (React StrictMode's double-mounted effect in dev,
// two receipts holding the same lead, a resolver restart) share the promise
// instead of paying twice — the same reason enrichLead() dedupes. Cleared when
// it settles, so a failure can be retried.
const _draftInFlight = new Map()

/**
 * THE ONE PLACE THAT KNOWS WHAT A REFUSAL LOOKS LIKE ON THE WIRE. Returns
 * `{ reason, category }` for a draft response that declined to write, or null
 * for anything else — an email, an error body, a shape nobody recognises.
 *
 * Exported because the card that renders the refusal (Chat.jsx#resolveLeadDraft)
 * has to answer the identical question about the identical body, and two copies
 * of "is this a refusal?" is exactly how a client ends up treating a refusal as a
 * failure on one path and a decision on the other — which, here, is the
 * difference between showing the rep the reason and sending a colleague a pitch.
 *
 * Deliberately a little generous about the spelling, because the cost of the two
 * mistakes is not symmetric: reading a real refusal as a fault falls back to the
 * template pitch, while reading an odd body as a refusal costs one card its
 * email and says so on screen. So `skip` in any of its truthy spellings counts,
 * and so does a bare `reason` on an emailless 200 — a body that took the trouble
 * to explain itself is a decision, whatever flag it forgot.
 *
 * It insists on ONE thing: that there is no email in the answer. A body carrying
 * a real subject AND a real bodyText is a draft, whatever rides alongside it —
 * a written email is never thrown away on the strength of a stray flag.
 */
export function refusalOf(data) {
  if (!data || typeof data !== 'object') return null
  if (String(data.subject || '').trim() && String(data.bodyText || '').trim()) return null
  const reason = [data.reason, data.skip_reason, data.skipReason, data.message, data.detail]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find(Boolean) || ''
  const flagged = data.skip === true || data.skipped === true || data.skip === 'true'
  if (!flagged && !reason) return null
  // On a refusal `category` is the SKIP category, not the campaign's (see the
  // note above). Bounded but never rewritten: the lambda already pulls an
  // invented category back to `not-a-prospect`, and a second opinion here would
  // only mean the receipt and the server disagree about what was decided.
  return { reason, category: String(data.category || '').trim().slice(0, 40) }
}

export async function draftLead(payload, { signal } = {}) {
  const p = payload || {}
  // The person is part of the request's identity: the same lead asked for
  // two different people (the rep switched the card) is two drafts, and the
  // second must not be handed the first one's promise.
  const who = `${p.lead?.recipient || p.lead?.email || ''}|${p.lead?.contact_name || ''}`.toLowerCase()
  const id = `${p.category || ''}::${p.key || ''}::${p.kind || 'initial'}::${p.touch || 1}::${who}`
  const shared = p.key ? _draftInFlight.get(id) : null
  if (shared) return shared
  const request = (async () => {
    const data = await apiFetch('draft', {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(p),
      signal,
    })
    if (data?.configured === false) {
      const err = new Error(data.error || 'Drafting is not configured on the API')
      err.status = 409
      throw err
    }
    if (refusalOf(data)) return data
    if (!data?.subject || !data?.bodyText) {
      // Neither an email nor a refusal: no subject, no body, no `skip`, no
      // reason. Nobody decided this — it is a fault, and it stays a throw, so
      // the caller falls back the way it does for any other broken answer
      // rather than putting a blank draft on a card.
      throw new Error('The draft API returned no email')
    }
    return data
  })()
  if (p.key) _draftInFlight.set(id, request)
  try {
    return await request
  } finally {
    if (p.key) _draftInFlight.delete(id)
  }
}

// GET /enrich/draft-spec — the draft prompt AS DEPLOYED RIGHT NOW: its id, the
// sha of its text, the model, the handler path and its source sha, and the
// prompt text itself. The Developer view fetches this on demand and compares
// the sha against the exec record stored on a receipt, so it can say "this is
// the exact prompt that wrote this email" or "the prompt has changed since" —
// never silently show today's prompt as if it were yesterday's.
export async function draftSpec({ signal } = {}) {
  return apiFetch('draft-spec', { headers: await authHeaders(), signal })
}

// GET /enrich/source — the SOURCE TEXT of named top-level functions and
// constants of a deployed lambda's handler.py (enrich/handler.py#_source): the
// enrich API's own file as its container loaded it, or the enrich-worker's
// straight from its deployed package. `code` is the sha of that text, so the
// Developer view can hold it against the sha an exec record carries and say
// whether this is the exact revision that ran. Read-only; the server returns
// whole definitions only.
const _sourceCache = new Map()
export async function lambdaSource(fn, symbols, { signal } = {}) {
  const names = [...new Set((symbols || []).map((s) => String(s || '').trim()).filter(Boolean))]
  if (!names.length) return { function: fn, symbols: [], missing: [] }
  const id = `${fn}::${names.join(',')}`
  if (_sourceCache.has(id)) return _sourceCache.get(id)
  const qs = new URLSearchParams({ function: fn || '', symbols: names.join(',') })
  const request = apiFetch(`source?${qs}`, { headers: await authHeaders(), signal }).catch((err) => {
    _sourceCache.delete(id)
    throw err
  })
  _sourceCache.set(id, request)
  return request
}

// Poll one batch. Returns { jobs: [{ jobId, company, status, rankedScore,
// phonesPending, result, phones, error }], complete }.
export async function enrichStatus(batchId, { signal } = {}) {
  return apiFetch(`status?batchId=${encodeURIComponent(batchId)}`, {
    headers: await authHeaders(),
    signal,
  })
}

// A still-running batch is remembered so "Run in background" is honest: closing
// the panel keeps the batch, and clicking Enrich again resumes it (polling the
// same batchId) instead of starting — and paying for — a fresh one.
//
// Per account: resuming a batch writes its results into the signed-in user's
// enriched/enrichment stores and stamps the activity event with them as actor,
// so a global handle would let whoever signs in next inherit someone else's
// paid research under their own name.
const ACTIVE_BASE = 'revailosales.enrichBatch'
const activeKey = () => scopedKey(ACTIVE_BASE)

export function saveActiveBatch(batchId, category) {
  try {
    localStorage.setItem(activeKey(), JSON.stringify({ batchId, category, at: Date.now() }))
  } catch {
    /* ignore */
  }
}

export function loadActiveBatch() {
  try {
    const v = JSON.parse(localStorage.getItem(activeKey()) || 'null')
    // Ignore anything missing or older than 30 minutes.
    if (!v?.batchId || Date.now() - (v.at || 0) > 30 * 60 * 1000) return null
    return v
  } catch {
    return null
  }
}

export function clearActiveBatch() {
  try {
    localStorage.removeItem(activeKey())
  } catch {
    /* ignore */
  }
}

// ── the operation runs (the chat's operations on Step Functions) ─────────────
// POST /enrich/operation-start, GET /enrich/operation-run, POST
// /enrich/operation-stop — the chat's Daily Tasks, Follow-Up and Initial
// outreach run on the server as the daily-tasks, follow-up and
// initial-outreach machines; lib/operationRuns.js starts one, watches it pass
// by pass and turns the finished run into the usual receipt. Members only on
// the server, and a run is read back only by whoever started it.

// { runId, machine, operationId, executionArn, startedAt } — or throws with
// the server's status (a 404 means the routes are not deployed yet).
export async function startOperationRun(body = {}, { signal } = {}) {
  return apiFetch('operation-start', {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  })
}

// { status, error, run, passes, progress, receipt } — receipt only once done.
export async function operationRun(runId, { signal } = {}) {
  return apiFetch(`operation-run?run_id=${encodeURIComponent(runId)}`, {
    headers: await authHeaders(),
    ...(signal ? { signal } : {}),
  })
}

export async function stopOperationRun(runId) {
  return apiFetch('operation-stop', {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ runId }),
  })
}
